import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "zodios/client";
import { css } from "../../../styled-system/css";
import { Flex } from "../../../styled-system/jsx";
import { Button } from "components/core/basic/Button";
import { TextField } from "components/fields/base/TextField";
import { successToast } from "utils/toasts";
import { getAPIKeys } from "utils/getUrlPrefix";
import { setTokens } from "redux/reducers/authReducers";
import { setChannel } from "redux/reducers/channel";
import { refreshAmzOnboardStatus, updateAmzOnboardingStatus } from "redux/reducers/amzOnboardReducer";
import { setErrors } from "redux/reducers/errorReducers";
import setAuthToken from "utils/setAuthToken";
import { setCurrentUser } from "redux/reducers/userReducer";
import userMarketPlace from "utils/userMarketPlace";
import { Link } from "react-router-dom";
import { HoverInfo } from "components/core/basic/Tooltip";
import { AuthLayout } from "layouts/AuthLayout";

const apiKeys = getAPIKeys();

const CLIENT_ID = apiKeys["API_AUTH_CLIENT_ID"];
const CLIENT_SECRET = apiKeys["API_AUTH_SECRET"];

interface UserInvitationFormData {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

interface AcceptUserInvitationVariables {
  inviteToken: string;
  grant_type: "password";
  client_id: string;
  client_secret: string;
  first_name: string;
  last_name: string;
  password: string;
}

const INITIAL_FORM_DATA: UserInvitationFormData = { firstName: "", lastName: "", password: "", confirmPassword: "" };

const InviteForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [error, setError] = useState("");

  const acceptUserInviteMutation = useMutation({
    mutationFn: async (variables: AcceptUserInvitationVariables) => {
      const acceptInviteRes = await apiClient.acceptUserInvitation(variables);

      const passwordLoginInfo = {
        grant_type: "password" as const,
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        username: acceptInviteRes.email_address,
        password: formData.password,
      };

      const tokensRes = await apiClient.login(passwordLoginInfo);
      const { access_token, refresh_token } = tokensRes;

      refreshAmzOnboardStatus(updateAmzOnboardingStatus, access_token, setErrors);

      const authRes = await apiClient.fetchLoginUserInfo({
        custom_api_request: "login_user_info",
        user_email: acceptInviteRes.email_address,
      });

      const tokens = {
        access_token,
        refresh_token,
      };

      const channel = authRes.channel.toLowerCase();
      localStorage.setItem("Tokens", JSON.stringify(tokens));

      setAuthToken(access_token);
      setCurrentUser(authRes);
      setTokens(tokens);
      setChannel(channel);

      localStorage.setItem("channel", "amazon");
      userMarketPlace(access_token, () => {
        if (authRes.permissions === "reports") {
          window.location.href = "/user/report/advertisement";
        } else {
          window.location.href = "/user/dashboard";
        }
      });
    },
    onError: (res: { response: { data: { email?: string[]; password?: string[] } } }) => {
      const errors = res.response.data;

      if (errors.email && errors.email[0]) {
        setError(errors.email[0]);
        return;
      }

      if (errors.password && errors.password[0]) {
        setError(errors.password[0]);
        return;
      }

      setError(
        "Something went wrong while accepting your user invitation. Please try again or contact customer success.",
      );
    },
    onSuccess: () => {
      successToast({ message: "User invitation successfully accepted." });
    },
  });

  const handleChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <AuthLayout title={"Welcome to Trellis"} subtitle={"Complete your invitation by confirming your password."}>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          const { firstName, lastName, password, confirmPassword } = formData;

          if (password !== confirmPassword) {
            setError("Password and password confirmation do not match.");
            return;
          }

          const queryParams = new URLSearchParams(window.location.search);
          const token = queryParams.get("token");

          if (!token) {
            setError("A token is required");
            return;
          }

          acceptUserInviteMutation.mutate({
            grant_type: "password",
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            first_name: firstName,
            last_name: lastName,
            password,
            inviteToken: token,
          });
        }}
      >
        <Flex gap={"medium"} direction={"column"}>
          <TextField
            label={"First Name"}
            name={"firstName"}
            type={"text"}
            onChange={(value) => handleChange("firstName", value)}
          />
          <TextField
            label={"Last Name"}
            name={"lastName"}
            type={"text"}
            onChange={(value) => handleChange("lastName", value)}
          />
          <TextField
            label={
              <span className={css({ display: "flex", gap: "small" })}>
                Password{" "}
                <HoverInfo iconClassName={css({ color: "grey", fontSize: "small" })}>
                  Password must be at least 8 characters long and include at least one uppercase letter, one lowercase
                  letter, and one number.
                </HoverInfo>
              </span>
            }
            name={"password"}
            type={"password"}
            onChange={(value) => handleChange("password", value)}
          />
          <TextField
            label={"Confirm Password"}
            name={"confirmPassword"}
            type={"password"}
            onChange={(value) => handleChange("confirmPassword", value)}
          />
        </Flex>
        <Button
          isPending={acceptUserInviteMutation.isPending}
          isDisabled={Object.values(formData).some((fieldValue) => !fieldValue.trim().length)}
          className={css({ width: "100%", marginTop: "large" })}
          type="submit"
        >
          Complete Signup
        </Button>
      </form>
      <div className={css({ width: "100%", marginTop: "medium", textAlign: "center" })}>
        <Link to="/">Sign In Instead</Link>
      </div>
      {error && <p className={css({ paddingY: "large", textAlign: "center" })}>{error}</p>}
    </AuthLayout>
  );
};

export default InviteForm;
